import { formatCurrencyAmount, formatDepositAmount } from "@/client/money";
import type { DepositItem, PosBalance } from "@/client/types";
import { ArrowRightIcon, ReceiptIcon } from "@/components/icons";
import {
  projectedOutstandingBalanceMinor,
  projectedRefundDueMinor,
  terminalTransactionForBalance,
} from "@/shared/balance";

type CheckoutPanelProps = {
  items: DepositItem[];
  currency: string;
  maxRefundCents: number;
  settlementRoute: string;
  balance: PosBalance | null;
  isSubmitting: boolean;
  onComplete: () => void;
};

export function CheckoutPanel({
  items,
  currency,
  maxRefundCents,
  settlementRoute,
  balance,
  isSubmitting,
  onComplete,
}: CheckoutPanelProps) {
  const selected = items.filter((item) => item.quantity > 0);
  const total = selected.reduce(
    (sum, item) => sum + item.depositCents * item.quantity,
    0,
  );
  const account = balance
    ? {
        totalAmountMinor: balance.totalCents,
        paidAmountMinor: balance.paidCents,
      }
    : null;
  const outstanding = account
    ? projectedOutstandingBalanceMinor(account, total)
    : null;
  const refundDue = account ? projectedRefundDueMinor(account, total) : null;
  const terminalTransaction =
    outstanding === null ? null : terminalTransactionForBalance(outstanding);
  // The configured maximum limits money paid out, not credit that merely
  // offsets an existing positive account balance.
  const isOverLimit = refundDue !== null && refundDue > maxRefundCents;
  const hasSettleableBalance = outstanding !== null && outstanding !== 0;
  const hasWork = selected.length > 0 || hasSettleableBalance;
  const canPay = hasWork && !isOverLimit;

  return (
    <aside className="checkout-panel">
      <div className="receipt-paper">
        <div className="receipt-heading">
          <div className="receipt-icon">
            <ReceiptIcon />
          </div>
          <div>
            <span>Terminalausgleich</span>
            <strong>Custos</strong>
          </div>
        </div>
        <div className="line-items">
          {selected.length === 0 ? (
            <p className="empty-receipt">
              Keine zusätzlichen Pfandartikel. Der vorhandene Bon kann direkt
              über Custos ausgeglichen werden.
            </p>
          ) : (
            selected.map((item) => (
              <div className="receipt-row" key={item.id}>
                <span>
                  {item.quantity}× {item.name}
                </span>
                <strong>
                  {formatDepositAmount(
                    item.quantity * item.depositCents,
                    currency,
                  )}
                </strong>
              </div>
            ))
          )}
        </div>
        <div className="receipt-total">
          <span>Gutschrift im Bon</span>
          <strong>{formatDepositAmount(total, currency)}</strong>
        </div>
        <dl className="balance-summary">
          <div>
            <dt>Bon gesamt</dt>
            <dd>
              {balance
                ? formatCurrencyAmount(balance.totalCents, balance.currency)
                : "—"}
            </dd>
          </div>
          <div>
            <dt>Bereits bezahlt</dt>
            <dd>
              {balance
                ? formatCurrencyAmount(balance.paidCents, balance.currency)
                : "—"}
            </dd>
          </div>
          <div>
            <dt>Netto-Rest</dt>
            <dd>
              {outstanding === null
                ? "—"
                : formatCurrencyAmount(
                    outstanding,
                    balance?.currency ?? currency,
                  )}
            </dd>
          </div>
          <div className="due">
            <dt>
              {terminalTransaction?.type === "payment"
                ? "Kartenzahlung"
                : "Rückerstattung"}
            </dt>
            <dd>
              {refundDue === null
                ? "wird am Terminal berechnet"
                : formatCurrencyAmount(
                    terminalTransaction?.amountMinor ?? refundDue,
                    balance?.currency ?? currency,
                  )}
            </dd>
          </div>
        </dl>
        {isOverLimit && (
          <p className="limit-message">
            Das Maximum pro Rückgabe beträgt{" "}
            {formatCurrencyAmount(maxRefundCents, currency)}.
          </p>
        )}

        <p className="settlement-route">
          Finanzweg: <strong>{settlementRoute}</strong>
        </p>
      </div>

      <div className="checkout-actions">
        <button
          className="button button-primary"
          disabled={!canPay || isSubmitting}
          type="button"
          onClick={onComplete}
        >
          {isSubmitting
            ? "Wird angefordert …"
            : terminalTransaction?.type === "payment"
              ? `${formatCurrencyAmount(terminalTransaction.amountMinor, balance?.currency ?? currency)} bezahlen`
              : terminalTransaction?.type === "refund"
                ? `${formatCurrencyAmount(terminalTransaction.amountMinor, balance?.currency ?? currency)} erstatten`
                : "Bon abschließen"}{" "}
          <ArrowRightIcon />
        </button>
      </div>
    </aside>
  );
}
